/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2020, Zumper
 * @description   Message Form Inputs
 */

import { usePrevious } from '@chakra-ui/react'
import type { selectActiveMessageableListings } from '@zcode-ui/perm-address'
import { getUser, selectNow } from '@zcode-ui/web-zumper-global-state'
import {
  Input,
  MoveInDateInput,
  type MoveInDateInputProps,
  TextArea,
} from '@zcode-ui/web-zumper-ui'
import type { Origin } from '@zcode/nr-utils'
import {
  type APPLY,
  type MESSAGE,
  TOUR,
  type WAITLIST,
} from '@zcode/nr/es/shared/constants/message'
import { cn } from '@zcode/nr/es/shared/utils/css'
import type {
  FloorplanListing,
  Listable,
  Listing,
  NormalFloorplanListingData,
  Property,
} from '@zcode/state-type'
import { isDate as _isDate, differenceInDays, format, parseISO, startOfDay } from 'date-fns'
import type { FormikErrors, FormikTouched } from 'formik'
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { SavedCustomMessageToggle } from '../../../../features/SavedCustomMessage/components/SavedCustomMessageToggle'
import { useSavedCustomMessageToggle } from '../../../../features/SavedCustomMessage/hooks/useSavedCustomMessageToggle'
import { selectCustomMessageChecked } from '../../../../features/SavedCustomMessage/module/selectors'
import { getRefreshedMoveInDate } from '../../../../modules/profile/thunks/getRefreshedMoveInDate'
import { useDispatch } from '../../../../modules/useDispatch'
import { InputEmailChecker } from '../../../../packages/Inputs/InputEmail/InputEmailChecker'
import { useMaskedInput } from '../../../../packages/Inputs/MaskedInput/helpers/useMaskedInput'
import { moveInOptionalValidator } from '../../../../packages/Inputs/validator/moveIn'
import type { getUrlParams } from '../../../../utils/urls/getUrlParams'
import { useHasInstantTourBooking } from '../TourOptions/useHasInstantTourBooking'
import type { Values } from '../helpers'
import { FAR_AWAY_MOVE_IN_THRESHOLD } from './FarAwayMoveIn/FarAwayMoveIn'
import { FarAwayMoveInContainer } from './FarAwayMoveIn/FarAwayMoveInContainer'
import { FloorplanInput as NewFloorplanInput } from './FloorplanInput'
import { FormRow } from './FormRow'
import { TourSummary } from './TourSummary'
import { UnitInput } from './UnitInput'
import { selectFloorplanFromUrlParams } from './selectFloorplanFromUrlParams'

// @ts-expect-error
const isDate: (anything: unknown) => anything is Date = _isDate

export interface FormInputsProps {
  avatarName: string
  defaultMessageRef: React.MutableRefObject<string | undefined>
  entity: Listable | Property
  equalCTAIntent: typeof APPLY | typeof MESSAGE | typeof TOUR | typeof WAITLIST | undefined
  errors: FormikErrors<Values>
  floorplanRequired: boolean
  floorplans: ReadonlyArray<FloorplanListing<NormalFloorplanListingData>> | undefined
  fullName: string
  hideFloorplans: boolean | undefined
  hideInputs: boolean
  initialValues: Values
  initialPhoneValue: string | undefined
  isBuilding: boolean
  isLoggedIn: boolean
  isMobile: boolean
  isModal: boolean | undefined
  isMultifamily: boolean
  listings: ReturnType<typeof selectActiveMessageableListings>
  moveInRequired: boolean
  onChange: (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ClipboardEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => void
  onlyShowFloorplanGroup: boolean | undefined
  onSetFloorplanUserSelected: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onShowInputs: () => void
  onTrackConversion: (
    event:
      | React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void
  origin: Origin
  phoneNumberRequired: boolean
  selectedFloorplan: FloorplanListing<NormalFloorplanListingData> | undefined
  selectedListing: Listing | null
  selectListing: (listingId: number) => void
  setFieldTouched: (
    key: keyof Values,
    touched: boolean,
    shouldValidate?: boolean | undefined
  ) => void
  setFieldValue: <
    KeyValuePair extends NonNullable<{ [Key in keyof Values]: [Key, Values[Key]] }[keyof Values]>,
  >(
    key: KeyValuePair[0],
    value: KeyValuePair[1],
    shouldValidate?: boolean | undefined
  ) => void
  shouldChangeText: boolean
  shouldRenderDateInputs: boolean
  showAgent?: boolean
  showMessageInput: boolean
  showMultifamilyTour: boolean | undefined
  touched: FormikTouched<Values>
  urlParams: ReturnType<typeof getUrlParams>
  values: Values
}

export const FormInputs = ({
  listings,
  selectListing,
  avatarName,
  defaultMessageRef,
  equalCTAIntent,
  entity,
  errors,
  floorplanRequired,
  floorplans,
  fullName,
  hideFloorplans,
  hideInputs,
  initialPhoneValue,
  isBuilding,
  isLoggedIn,
  isMobile,
  moveInRequired,
  onChange,
  onlyShowFloorplanGroup,
  onSetFloorplanUserSelected,
  onShowInputs,
  onTrackConversion,
  origin,
  phoneNumberRequired,
  setFieldValue,
  setFieldTouched,
  shouldChangeText,
  shouldRenderDateInputs,
  showAgent,
  showMessageInput,
  showMultifamilyTour,
  touched,
  values,
  selectedListing,
  urlParams,
}: FormInputsProps) => {
  useSavedCustomMessageToggle(values.messageModified, !values.msg)

  const selectedFloorplanFromParam = useMemo(() => {
    const selectedFloorplans = selectFloorplanFromUrlParams(urlParams, floorplans)
    return selectedFloorplans?.length ? selectedFloorplans[0] : undefined
  }, [floorplans, urlParams])
  const { hasInstantTourAvailability } = useHasInstantTourBooking(entity, equalCTAIntent !== TOUR)

  const [floorplanFromParam, setFloorplanFromParam] = useState(selectedFloorplanFromParam)
  const dispatch = useDispatch()

  const date = !values.moveIn
    ? undefined
    : isDate(values.moveIn)
      ? values.moveIn
      : parseISO(values.moveIn)
  const now = useSelector(selectNow)
  const isFarAwayMoveInDate = date
    ? differenceInDays(date, startOfDay(new Date(now))) > FAR_AWAY_MOVE_IN_THRESHOLD
    : false
  const customMessage = useSelector(selectCustomMessageChecked)

  if (
    defaultMessageRef &&
    values.msg &&
    values.msg !== defaultMessageRef.current &&
    !values.messageModified
  ) {
    defaultMessageRef.current = values.msg
  }
  const onMessageBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (!values.msg && defaultMessageRef?.current) {
        setFieldValue('msg', defaultMessageRef.current)
        setFieldValue('messageModified', false)
      }
      onTrackConversion(e)
    },
    [defaultMessageRef, onTrackConversion, setFieldValue, values.msg]
  )
  const onMessageFocus = useCallback(() => {
    // only clear the message on focus if it's not a custom message
    if (values.msg && values.msg === defaultMessageRef?.current && !customMessage) {
      setFieldValue('msg', '')
    }
  }, [defaultMessageRef, setFieldValue, values.msg])

  useEffect(() => {
    let canceled = false
    if (!touched.moveIn) {
      void (async () => {
        const savedMoveInDate = dispatch(
          (_, getState) => getUser(getState()).tenantProfile?.moveInDate
        )
        // If there's an invalid saved move-in date, getInitialFormValues (which is synchronous)
        // will have ignored it and just used the default move-in date/text. It's in that case
        // that we want to use getRefreshedMoveInDate() and generate a new move-in date based on
        // their previously-selected move-in text
        if (!savedMoveInDate) {
          return
        }
        const shouldUpdateMoveInDate = !moveInOptionalValidator.isValidSync(savedMoveInDate)
        const { refreshedMoveInDate, savedMoveInText } = await dispatch(getRefreshedMoveInDate())
        if (canceled || !refreshedMoveInDate) {
          return
        }
        if (
          shouldUpdateMoveInDate &&
          (!values.moveIn ||
            (typeof values.moveIn === 'string'
              ? values.moveIn
              : format(values.moveIn, 'yyyy-MM-dd')
            ).substring(0, 10) !== format(refreshedMoveInDate, 'yyyy-MM-dd'))
        ) {
          setFieldValue('moveIn', refreshedMoveInDate)
        }
        if (values.moveInText !== savedMoveInText) {
          setFieldValue('moveInText', savedMoveInText)
        }
      })()
    }
    return () => {
      canceled = true
    }
  }, [dispatch, setFieldValue, touched.moveIn, values.moveIn, values.moveInText])
  const onSelectMoveInDate: MoveInDateInputProps['onSelectMoveInDate'] = useCallback(
    (date, text) => {
      setFieldTouched('moveIn', true)
      setFieldValue('moveIn', date)
      setFieldValue('moveInText', text)
    },
    [setFieldValue, setFieldTouched]
  )

  const listingOrFloorplanInput =
    listings?.length > 1 ? (
      <UnitInput
        error={errors.selectedFloorplanId}
        isMobile={isMobile}
        listings={listings}
        onChange={(e) => {
          selectListing(Number(e.target.value))
          onTrackConversion(e)
          onChange(e)
        }}
        selectedListing={selectedListing}
      />
    ) : isBuilding ? (
      <div
        className={cn(
          'w-full',
          !floorplans && 'min-h-[65px] border mb-2.5 rounded-[5px] border-solid border-[#d5d9dc]'
        )}
      >
        {!hideFloorplans && (
          <NewFloorplanInput
            error={errors.selectedFloorplanId}
            floorplans={floorplans}
            isInvalid={!!errors.selectedFloorplanId && touched.selectedFloorplanId}
            isMobile={isMobile}
            isRequired={floorplanRequired}
            onChange={(e) => {
              onSetFloorplanUserSelected(e)
              onChange(e)
            }}
            onTrackConversion={onTrackConversion}
            onlyShowFloorplanGroup={onlyShowFloorplanGroup}
            selectedFloorplanFromUrl={floorplanFromParam}
            selectedFloorplanId={values.selectedFloorplanId}
            setFieldValue={setFieldValue}
            setFloorplanFromParam={setFloorplanFromParam}
            shouldChangeText={shouldChangeText}
          />
        )}
      </div>
    ) : null

  const { maskedValue: maskedPhoneValue, onChange: onChangePhone } = useMaskedInput({
    mask: '(999) 999-9999 ext. 999999',
    onChange,
    value: values.phone,
  })
  const phoneInput = (
    <Input
      autoComplete="tel"
      error={errors.phone}
      hideRightElement
      isInvalid={!!errors.phone && touched.phone}
      isRequired={phoneNumberRequired}
      label={`Phone${phoneNumberRequired ? '' : ' (optional)'}`}
      name="phone"
      onBlur={onTrackConversion}
      onChange={onChangePhone}
      placeholder="(555) 555-5555"
      type="tel"
      value={maskedPhoneValue}
    />
  )

  const setEmail = useCallback(
    (value: string) => {
      setFieldValue('email', value)
    },
    [setFieldValue]
  )

  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const wasHideInputs = usePrevious(hideInputs)
  useEffect(() => {
    if (wasHideInputs && !hideInputs) {
      nameInputRef.current?.focus()
    }
  }, [hideInputs, nameInputRef, wasHideInputs])

  const messageFieldId = useId()

  return (
    <>
      {equalCTAIntent === TOUR && isMobile && (
        <TourSummary
          {...(hasInstantTourAvailability
            ? { mb: 6 }
            : {
                borderBottomColor: 'gray.150',
                borderBottomWidth: '1px',
                borderBottomStyle: 'solid',
                mb: 4,
                pb: 4,
              })}
          hideTimeAndType={!hasInstantTourAvailability}
          tz={entity.tz}
        />
      )}
      <>
        {hideInputs ? (
          <>
            {isLoggedIn ? (
              <FormRow
                borderWidth="1px"
                borderStyle="solid"
                borderColor="gray.150"
                className={cn('flex', 'mb-2.5', 'p-2.5', 'rounded-[5px]', 'bg-gray-0')}
              >
                <div
                  className={cn(
                    'flex',
                    'items-center',
                    'justify-center',
                    'w-10',
                    'h-10',
                    'text-[#00486a]',
                    'font-medium',
                    'text-[17px]',
                    'leading-[25px]',
                    'p-[5px]',
                    'rounded-[20px]',
                    'bg-neutral-200'
                  )}
                >
                  {avatarName.toUpperCase()}
                </div>
                <div
                  className={cn(
                    'text-[#2a2b2c]',
                    'text-base',
                    'leading-[21px]',
                    'pl-2.5',
                    'pr-[5px]',
                    'py-0',
                    'font-regular',
                    'my-auto'
                  )}
                >
                  {fullName}
                </div>
              </FormRow>
            ) : (
              <FormRow>
                <Input
                  hideRightElement={!values.name}
                  isRequired
                  label={values.name ? 'Contact info' : 'Full name'}
                  onBlur={onTrackConversion}
                  onClick={onShowInputs}
                  onFocus={onShowInputs}
                  onIconClick={onShowInputs}
                  placeholder="First Last"
                  value={values.name}
                  width="100%"
                />
              </FormRow>
            )}
            {(errors.phone || initialPhoneValue !== values.phone) &&
              (!!values.name || touched.name) && <FormRow>{phoneInput}</FormRow>}
          </>
        ) : (
          <>
            <FormRow className="min-[768px]:max-[1024px]:flex-col">
              <Input
                autoComplete="name"
                error={errors.name}
                hideRightElement
                isInvalid={!!errors.name && touched.name}
                isRequired
                label="Full name"
                name="name"
                onBlur={onTrackConversion}
                onChange={onChange}
                onIconClick={onShowInputs}
                placeholder="First Last"
                ref={nameInputRef}
                value={values.name}
                width="100%"
              />
            </FormRow>
            <FormRow>
              <InputEmailChecker setValue={setEmail} value={values.email}>
                <Input
                  autoComplete="email"
                  error={errors.email}
                  hideRightElement
                  isInvalid={!!errors.email && touched.email}
                  isRequired
                  label="Email"
                  name="email"
                  onBlur={onTrackConversion}
                  onChange={onChange}
                  placeholder="name@email.com"
                  type="email"
                  value={values.email}
                />
              </InputEmailChecker>
            </FormRow>
            <FormRow>{phoneInput}</FormRow>
          </>
        )}
        {shouldRenderDateInputs && (
          <FormRow display="flex" gap={2}>
            <MoveInDateInput
              error={errors.moveIn}
              isInvalid={!!errors.moveIn && !!values.moveIn}
              isMobile={isMobile}
              isRequired={moveInRequired}
              moveInDate={values.moveIn}
              moveInText={values.moveInText}
              onSelectMoveInDate={onSelectMoveInDate}
            />
            {listingOrFloorplanInput}
          </FormRow>
        )}
        {isFarAwayMoveInDate && <FarAwayMoveInContainer origin={origin} />}
        {showMessageInput && (
          <>
            <FormRow>
              <TextArea
                className="w-full"
                cols={40}
                defaultValue={defaultMessageRef?.current}
                error={errors.msg}
                isDefault={!customMessage}
                isInvalid={!!errors.msg && touched.msg}
                label="Custom message"
                name="msg"
                onBlur={onMessageBlur}
                onChange={(e) => {
                  setFieldValue('messageModified', true)
                  onChange(e)
                }}
                onFocus={onMessageFocus}
                placeholder="Tailored messages catch attention–share your rental needs!"
                rows={showAgent || isMobile || showMultifamilyTour ? 3 : 4}
                textAreaProps={{ id: messageFieldId }}
                value={values.msg}
              />
            </FormRow>
            <SavedCustomMessageToggle />
          </>
        )}
      </>
    </>
  )
}
